import React, { useEffect, useState } from 'react';
import { ClienteFacade, Cliente, ClienteDTO, TipoPessoa, EstadoCivil, FaseFunil } from '../../facades/ClienteFacade';
import Sidebar from '../Sidebar';

// Estado inicial para um novo formulário de cliente
const initialState: ClienteDTO = {
    nome: '',
    cpfCnpj: '',
    email: '',
    tipoPessoa: TipoPessoa.FISICA,
    aceitaMarketing: true,
    faseFunil: FaseFunil.LEAD_FRIO,
    estadoCivil: EstadoCivil.NAO_INFORMADO
};

// Componente de Abas
const Tab: React.FC<{ label: string; isActive: boolean; onClick: () => void }> = ({ label, isActive, onClick }) => (
    <button
        onClick={onClick}
        className={`px-4 py-2 font-bold text-sm rounded-t-lg transition-all duration-200
            ${isActive
            ? 'bg-blue-900 text-white shadow-inner'
            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
            }`}
    >
        {label}
    </button>
);


export const ClientesList: React.FC = () => {
    const [clientes, setClientes] = useState<Cliente[]>([]);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState<string | null>(null);
    const [sucesso, setSucesso] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editandoId, setEditandoId] = useState<number | null>(null);
    const [formData, setFormData] = useState<ClienteDTO>(initialState);
    const [salvando, setSalvando] = useState(false);
    const [activeTab, setActiveTab] = useState<'pessoal' | 'contato' | 'endereco' | 'marketing'>('pessoal');
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; id: number | null; nome: string }>({ isOpen: false, id: null, nome: '' });

    useEffect(() => {
        if (sucesso || erro) {
            const timer = setTimeout(() => {
                setSucesso(null);
                setErro(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [sucesso, erro]);

    useEffect(() => {
        carregarClientes();
    }, []);

    const carregarClientes = async () => {
        try {
            setLoading(true);
            const data = await ClienteFacade.listarTodos();
            setClientes(data || []);
        } catch (error: any) {
            console.error(error);
            setClientes([]);
            setErro("Sessão expirada ou acesso negado. Por favor, clique em 'Sair do Sistema' e faça login novamente.");
        } finally {
            setLoading(false);
        }
    };

    const mascaraCpfCnpj = (valor: string | undefined | null) => {
        if (!valor) return "";
        let v = valor.replace(/\D/g, "");
        if (v.length <= 11) {
            v = v.replace(/(\d{3})(\d)/, "$1.$2");
            v = v.replace(/(\d{3})(\d)/, "$1.$2");
            v = v.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
        } else {
            v = v.substring(0, 14);
            v = v.replace(/^(\d{2})(\d)/, "$1.$2");
            v = v.replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3");
            v = v.replace(/\.(\d{3})(\d)/, ".$1/$2");
            v = v.replace(/(\d{4})(\d{1,2})$/, "$1-$2");
        }
        return v;
    };

    const abrirModalNovo = () => {
        limparAlertas();
        setFormData(initialState);
        setEditandoId(null);
        setActiveTab('pessoal');
        setIsModalOpen(true);
    };

    const abrirModalEditar = (cliente: Cliente) => {
        limparAlertas();
        setFormData({ ...initialState, ...cliente, cpfCnpj: mascaraCpfCnpj(cliente.cpfCnpj) });
        setEditandoId(cliente.id);
        setActiveTab('pessoal');
        setIsModalOpen(true);
    };

    const limparAlertas = () => {
        setErro(null);
        setSucesso(null);
    };

    const handleSalvar = async (e: React.FormEvent) => {
        e.preventDefault();
        limparAlertas();
        try {
            setSalvando(true);
            if (editandoId) {
                await ClienteFacade.atualizar(editandoId, formData);
                setSucesso(`Cliente "${formData.nome}" atualizado com sucesso!`);
            } else {
                await ClienteFacade.criar(formData);
                setSucesso(`Cliente "${formData.nome}" cadastrado com sucesso!`);
            }
            setIsModalOpen(false);
            await carregarClientes();
        } catch (error) {
            setErro("Erro ao salvar. Verifique se os dados estão corretos ou se o CNPJ/CPF já existe.");
            setIsModalOpen(false);
        } finally {
            setSalvando(false);
        }
    };

    const abrirModalExcluir = (id: number, nome: string) => {
        limparAlertas();
        setDeleteModal({ isOpen: true, id, nome });
    };

    const confirmarExclusao = async () => {
        if (!deleteModal.id) return;
        try {
            await ClienteFacade.excluir(deleteModal.id);
            setSucesso(`Cliente "${deleteModal.nome}" foi excluído com sucesso!`);
            setDeleteModal({ isOpen: false, id: null, nome: '' });
            await carregarClientes();
        } catch (error: any) {
            setErro(`Não foi possível excluir "${deleteModal.nome}". O servidor recusou a ação.`);
            setDeleteModal({ isOpen: false, id: null, nome: '' });
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const isCheckbox = type === 'checkbox';
        // @ts-ignore
        const finalValue = isCheckbox ? e.target.checked : value;
        setFormData(prev => ({ ...prev, [name]: finalValue }));
    };


    return (
        <Sidebar>
            <header className="mb-8 border-b-2 border-gray-200 pb-4 flex justify-between items-end">
                <div>
                    <h2 className="text-3xl font-extrabold text-gray-800 tracking-tight">Gestão de Clientes (CRM)</h2>
                    <p className="text-gray-500 font-medium mt-1">Cadastre, gerencie e segmente seus leads e clientes.</p>
                </div>
                <button onClick={abrirModalNovo} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg shadow-lg transition-all flex items-center gap-2">
                    <span>➕</span> Novo Cliente
                </button>
            </header>

            {/* ... Alertas de erro e sucesso ... */}

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-900 mb-4"></div>
                    <p className="text-xl text-blue-900 font-bold animate-pulse">Buscando clientes...</p>
                </div>
            ) : (
                <div className="bg-white rounded-2xl shadow-lg overflow-x-auto border border-gray-100">
                    <table className="min-w-full text-left text-sm whitespace-nowrap">
                        <thead className="uppercase tracking-wider border-b-2 border-gray-200 bg-slate-50 text-gray-600 font-black">
                            <tr>
                                <th className="px-6 py-4">ID</th>
                                <th className="px-6 py-4">Nome / Razão Social</th>
                                <th className="px-6 py-4">CPF / CNPJ</th>
                                <th className="px-6 py-4">Contato Principal</th>
                                <th className="px-6 py-4">Fase do Funil</th>
                                <th className="px-6 py-4 text-center">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!Array.isArray(clientes) || clientes.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center py-8 text-gray-500 italic">Nenhum cliente localizado.</td>
                                </tr>
                            ) : (
                                clientes.map((cliente) => (
                                    <tr key={cliente.id} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                                        <td className="px-6 py-4 font-bold text-gray-500">#{cliente.id}</td>
                                        <td className="px-6 py-4 font-bold text-blue-900">{cliente.nome}</td>
                                        <td className="px-6 py-4 font-mono text-gray-700">{mascaraCpfCnpj(cliente.cpfCnpj)}</td>
                                        <td className="px-6 py-4 text-gray-600">{cliente.email || cliente.telefoneWhatsapp}</td>
                                        <td className="px-6 py-4">
                                            <span className={`py-1 px-3 rounded-full text-xs font-bold ${cliente.faseFunil === 'CLIENTE_ATIVO' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                {cliente.faseFunil?.replace('_', ' ')}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <button onClick={() => abrirModalEditar(cliente)} className="text-blue-600 hover:text-blue-900 font-bold mr-4">Editar</button>
                                            <button onClick={() => abrirModalExcluir(cliente.id, cliente.nome)} className="text-red-500 hover:text-red-700 font-bold">Excluir</button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden">
                        <div className="bg-blue-900 p-5 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                {editandoId ? '✏️ Editar Cliente' : '➕ Novo Cliente'}
                            </h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-blue-200 hover:text-white font-bold text-2xl leading-none">&times;</button>
                        </div>

                        <div className="p-2 bg-gray-100">
                            <Tab label="1. Dados Pessoais" isActive={activeTab === 'pessoal'} onClick={() => setActiveTab('pessoal')} />
                            <Tab label="2. Contato" isActive={activeTab === 'contato'} onClick={() => setActiveTab('contato')} />
                            <Tab label="3. Endereço" isActive={activeTab === 'endereco'} onClick={() => setActiveTab('endereco')} />
                            <Tab label="4. Marketing" isActive={activeTab === 'marketing'} onClick={() => setActiveTab('marketing')} />
                        </div>

                        <form onSubmit={handleSalvar} className="p-6 max-h-[60vh] overflow-y-auto">
                            {activeTab === 'pessoal' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="col-span-2">
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">Tipo de Pessoa</label>
                                        <select name="tipoPessoa" value={formData.tipoPessoa} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                                            {Object.values(TipoPessoa).map(t => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">{formData.tipoPessoa === 'FISICA' ? 'Nome Completo' : 'Razão Social'}</label>
                                        <input type="text" name="nome" required value={formData.nome} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg" />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">{formData.tipoPessoa === 'FISICA' ? 'CPF' : 'CNPJ'}</label>
                                        <input type="text" name="cpfCnpj" required value={formData.cpfCnpj} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg" />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">{formData.tipoPessoa === 'FISICA' ? 'RG' : 'Inscrição Estadual'}</label>
                                        <input type="text" name="rgIe" value={formData.rgIe || ''} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg" />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">{formData.tipoPessoa === 'FISICA' ? 'Data de Nascimento' : 'Data de Fundação'}</label>
                                        <input type="date" name="dataNascimento" value={formData.dataNascimento || ''} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg" />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">Estado Civil</label>
                                        <select name="estadoCivil" value={formData.estadoCivil} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                                            {Object.values(EstadoCivil).map(e => <option key={e} value={e}>{e.replace('_', ' ')}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">Profissão / Ramo de Atividade</label>
                                        <input type="text" name="profissao" value={formData.profissao || ''} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg" />
                                    </div>
                                </div>
                            )}

                            {activeTab === 'contato' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">E-mail Principal</label>
                                        <input type="email" name="email" required value={formData.email} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg" />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">Telefone / WhatsApp</label>
                                        <input type="text" name="telefoneWhatsapp" value={formData.telefoneWhatsapp || ''} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg" />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">Telefone Secundário</label>
                                        <input type="text" name="telefoneSecundario" value={formData.telefoneSecundario || ''} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg" />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">Instagram</label>
                                        <input type="text" name="instagram" value={formData.instagram || ''} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg" />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">LinkedIn</label>
                                        <input type="text" name="linkedin" value={formData.linkedin || ''} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg" />
                                    </div>
                                </div>
                            )}

                            {activeTab === 'endereco' && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="col-span-1">
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">CEP</label>
                                        <input type="text" name="cep" value={formData.cep || ''} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg" />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">Logradouro</label>
                                        <input type="text" name="logradouro" value={formData.logradouro || ''} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg" />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">Número</label>
                                        <input type="text" name="numero" value={formData.numero || ''} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg" />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">Complemento</label>
                                        <input type="text" name="complemento" value={formData.complemento || ''} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg" />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">Bairro</label>
                                        <input type="text" name="bairro" value={formData.bairro || ''} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg" />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">Cidade</label>
                                        <input type="text" name="cidade" value={formData.cidade || ''} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg" />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">Estado (UF)</label>
                                        <input type="text" name="estadoUf" maxLength={2} value={formData.estadoUf || ''} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg" />
                                    </div>
                                </div>
                            )}

                            {activeTab === 'marketing' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">Origem do Lead</label>
                                        <input type="text" name="origemLead" value={formData.origemLead || ''} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg" />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">Fase do Funil</label>
                                        <select name="faseFunil" value={formData.faseFunil} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                                            {Object.values(FaseFunil).map(f => <option key={f} value={f}>{f.replace('_', ' ')}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-gray-700 font-bold mb-2 text-sm">Tags de Interesse (separadas por vírgula)</label>
                                        <input type="text" name="tagsInteresse" value={formData.tagsInteresse || ''} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg" />
                                    </div>
                                    <div className="col-span-2 flex items-center mt-4">
                                        <input type="checkbox" id="aceitaMarketing" name="aceitaMarketing" checked={formData.aceitaMarketing} onChange={handleInputChange} className="h-5 w-5 text-blue-900 border-gray-300 rounded focus:ring-blue-900" />
                                        <label htmlFor="aceitaMarketing" className="ml-3 text-gray-700 font-bold">Cliente aceita receber e-mails e mensagens de marketing (LGPD)</label>
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-100">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2.5 text-gray-600 font-bold rounded-lg hover:bg-gray-100 transition-colors">Cancelar</button>
                                <button type="submit" disabled={salvando} className="px-6 py-2.5 bg-blue-900 hover:bg-blue-800 text-white font-bold rounded-lg shadow-md transition-all disabled:opacity-50">
                                    {salvando ? '⏳ Salvando...' : '💾 Salvar Dados'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ... Modal de Exclusão ... */}
        </Sidebar>
    );
};